import React, { useCallback, useEffect, useState } from 'react';

import { Modal as AntdModal, Button } from 'antd';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import translations from 'translations';
import utilsMessages from 'utils/messages';
import Modal from '../Modal/Modal';
import Chatting from './Chatting.svg';

const Textarea = styled.textarea`
  margin-top: 12px;
  border-radius: 4px;
  width: 100%;
  outline-width: 1px;
  padding: 10pt;
  outline-color: #d5d7d5;
  border-color: #d5d7d5;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const FooterOKButton = styled(Button)`
  width: 100%;
  max-width: 200px;
  height: 40px;
  font-weight: 500 !important;
  font-size: 16px !important;
  box-shadow: none !important;
  background: #43a047 !important;
  color: #ffffff !important;
`;

const FooterCancelButton = styled(Button)`
  width: 100%;
  max-width: 200px;
  height: 40px;
  font-weight: 500 !important;
  font-size: 16px !important;

  background: #ffffff !important;
  border-color: #d5d7d5 !important;
  color: #e2483c !important;
  box-shadow: none !important;
`;

export default ({
  isOpen,
  onSubmit,
  onClose,
  headerLabel,
  note,
  children,
  confirmLoading,
  disableOkButton,
}: {
  isOpen: boolean;
  disableOkButton?: boolean;
  onSubmit: (data) => void;
  onClose: () => void;
  onClickDeleteNote?: () => void;
  headerLabel: JSX.Element | string;
  note: string;
  confirmLoading?: boolean;
  children?: JSX.Element | JSX.Element[] | '';
}) => {
  const [noteValue, onChangeNote] = useState(note);
  const [isHavePrevNote, setIsHavePrevNote] = useState(false);

  useEffect(() => {
    onChangeNote(note);
  }, [note]);

  useEffect(() => {
    if (note && !!note?.trim()) {
      setIsHavePrevNote(true);
    } else {
      setIsHavePrevNote(false);
    }
  }, [note]);

  const deleteNote = useCallback(() => {
    AntdModal.confirm({
      autoFocusButton: null,
      okButtonProps: {
        danger: true,
      },
      title: translations(utilsMessages.titleRemoveNote),
      content: <p style={{ fontWeight: 300, marginTop: 16 }}>*{translations(utilsMessages.noteRemoveNote)}</p>,
      okText: translations(utilsMessages.yes),
      cancelText: translations(utilsMessages.no),
      onOk: () => {
        if (onSubmit) {
          onSubmit('');
          onChangeNote('');
        }
      },
    });
  }, [onSubmit, onChangeNote]);

  return (
    <Modal
      destroyOnClose={true}
      headerIcon={Chatting}
      headerLabel={headerLabel}
      confirmLoading={confirmLoading}
      disableOkButton={!noteValue}
      closable={true}
      isOpen={isOpen}
      submitLabel={<FormattedMessage {...utilsMessages.save} />}
      closeLabel={<FormattedMessage {...utilsMessages.cancel} />}
      onSubmit={() => {
        onSubmit(noteValue.trim());
        onChangeNote('');
      }}
      onClose={onClose}
      customStyles={`
         .ant-modal-close {
            top: 17px !important;
          }
      `}
      footer={
        <FooterContainer>
          {isHavePrevNote && (
            <FooterCancelButton type="default" onClick={deleteNote}>
              <FormattedMessage {...utilsMessages.removeNote} />
            </FooterCancelButton>
          )}
          <FooterOKButton
            type="primary"
            onClick={() => {
              onSubmit(noteValue.trim());
              onChangeNote('');
            }}
            loading={confirmLoading}
            disabled={disableOkButton || (!isHavePrevNote && !noteValue.trim())}
          >
            <FormattedMessage {...utilsMessages.save} />
          </FooterOKButton>
        </FooterContainer>
      }
    >
      <Textarea
        name="note"
        value={noteValue}
        onChange={(event: any) => onChangeNote(event.target.value)}
        rows={10}
        autofocus={true}
      />
      {children}
    </Modal>
  );
};
